import { Controller } from "@hotwired/stimulus"

// Phase 2.5 Tier 3 — Capital Request intake form. Handles the chip-based
// direct-S3 upload pattern shared with daily reports / bid intake (per
// CLAUDE.md). The same controller powers both:
//   - the internal "+ New Capital Request" form (auth'd)
//   - the public-token intake portal (no auth)
//
// presignUrlValue / registerUrlValue point at controller-specific
// endpoints — for the public form they include the portfolio token in
// the URL.
//
// On submit, files that are still uploading are blocked (toast); on
// success, files are registered server-side and an `attachment_ids[]`
// hidden input is appended for each so the form's `create` action can
// reparent them onto the saved CapitalRequest row.
export default class extends Controller {
  static targets = [
    "dropzone", "filePicker", "chipList", "hiddenInputs",
    "submitBtn", "saveDraftBtn"
  ]
  static values = {
    presignUrl: String,
    registerUrl: String
  }

  connect() {
    this._pendingUploads = 0
    this._attachmentIds = []
  }

  // ── Drag & drop ─────────────────────────────────────────────

  dragEnter(e) {
    e.preventDefault()
    this._setDropzoneActive(true)
  }

  dragOver(e) {
    e.preventDefault()
    this._setDropzoneActive(true)
  }

  dragLeave(e) {
    if (e.relatedTarget && this.dropzoneTarget.contains(e.relatedTarget)) return
    this._setDropzoneActive(false)
  }

  dropFiles(e) {
    e.preventDefault()
    this._setDropzoneActive(false)
    const files = Array.from(e.dataTransfer.files || [])
    if (files.length) this._upload(files)
  }

  openPicker() {
    this.filePickerTarget.click()
  }

  filesPicked(e) {
    const files = Array.from(e.target.files || [])
    if (files.length) this._upload(files)
    // Reset so picking the same file again still triggers change.
    e.target.value = ""
  }

  // ── Upload pipeline (presign → S3 PUT → register) ───────────

  async _upload(files) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    this._pendingUploads += files.length
    this._setSubmitDisabled(true)

    const placeholders = files.map(file => {
      const chip = this._createChip(file.name, { uploading: true })
      this.chipListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: f.type || "application/octet-stream",
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const file = files[i]
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": file.type || upload.content_type },
            body: file
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results
        .map((r, i) => r.status === "fulfilled" ? r.value : null)
        .filter(Boolean)

      if (successful.length === 0) throw new Error("All uploads failed")

      const registerRes = await fetch(this.registerUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      if (!registerRes.ok) throw new Error("Failed to register uploads")

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createChip(att.filename || `File #${att.id}`, {
          removable: true,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
            this._syncHidden()
          }
        })
        this.chipListTarget.appendChild(chip)
      })

      this._syncHidden()
    } catch (e) {
      console.error("[capital-request-form] upload error:", e)
      placeholders.forEach(p => p.remove())
      window.alert(`Upload failed: ${e.message || e}`)
    } finally {
      this._pendingUploads -= files.length
      if (this._pendingUploads <= 0) {
        this._pendingUploads = 0
        this._setSubmitDisabled(false)
      }
    }
  }

  _syncHidden() {
    this.hiddenInputsTarget.innerHTML = ""
    this._attachmentIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "attachment_ids[]"
      input.value = id
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  _setDropzoneActive(active) {
    if (!this.hasDropzoneTarget) return
    this.dropzoneTarget.classList.toggle("border-brand-400", active)
    this.dropzoneTarget.classList.toggle("bg-brand-50", active)
    this.dropzoneTarget.classList.toggle("border-gray-200", !active)
  }

  _setSubmitDisabled(disabled) {
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = disabled
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = disabled
  }

  _createChip(name, { removable = false, uploading = false, onRemove = null } = {}) {
    const chip = document.createElement("div")
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs ${uploading ? "bg-brand-50 text-brand-700" : "bg-gray-100 text-gray-700"}`

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncated = name.length > 28
      ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf("."))
      : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${name}">${truncated}</span>${removeBtn}`

    if (removable && onRemove) {
      chip.querySelector("[data-remove-chip]")?.addEventListener("click", e => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }
}
